// 求人編集の「公開」処理・検索インデックス再構築ロジック。
// 旧UI(navigation/index.tsx)・新UI(navigation/HorizontalNav.tsx)の両方から参照される唯一の実体。
// ロジックは navigation/index.tsx から一字一句そのまま移設したものであり、挙動は変更していない。
import { collection, doc, getDoc, getDocs, getFirestore, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { jobPostingYearCollection } from '../../2.jobPostings/jobPostingRef';
import { PREFECTURES, prefFromAddress } from '../../../../type';

// 学生アプリ「見つける > 求人」フィルター用。クーポン/会社説明会/スタイル写真の
// 有無(会社単位)を searchIndex.hasCoupon / hasInfoSession / hasStylePhoto に入れる。
// 新規公開・公開済み求人票の確定(searchIndexSync.ts)で呼ぶほか、非公開→再公開で
// searchIndex ドキュメントが作り直されたときにも最新値に戻すために使う。
// coupons/infoSessions の追加・削除そのものは functions/searchIndexHasFlags.js が
// 都度 searchIndex へ反映するため、ここは「ドキュメント自体が無かった期間」を
// 埋めるための再計算。
export async function computeHasFlags(db: ReturnType<typeof getFirestore>, companyId: string) {
  const [couponsSnap, infoSessionsSnap, postsSnap] = await Promise.all([
    getDocs(query(collection(db, "coupons"), where("companyId", "==", companyId))),
    getDocs(query(collection(db, "infoSessions"), where("companyId", "==", companyId))),
    getDocs(query(collection(db, "posts"), where("companyId", "==", companyId), where("post_student", "==", false))),
  ]);

  return {
    hasCoupon: !couponsSnap.empty,
    hasInfoSession: !infoSessionsSnap.empty,
    hasStylePhoto: !postsSnap.empty,
  };
}

export const sections = [
  "店舗概要",
  "募集店舗",
  "会社の魅力3選",
  "数字で見る",
  "写真で見る",
  "インタビュー",
  "求める人物像",
  "応募設定",
];

export async function publicDoc(documentId: string) {
  const db = getFirestore();

  const recruitDocRef = doc(db, "recruits", documentId + "_2027");
  const recruitSnap = await getDoc(recruitDocRef);

  if (!recruitSnap.exists()) {
    return;
  }

  const recruitData = recruitSnap.data();
  if (!recruitData) return;

  // ---------- ⑥ Firestore 更新 ----------
  await updateDoc(recruitDocRef, {
    recruitStatus: 1,
  });

  await searchIndexRegister(documentId);
}

export async function searchIndexRegister(targetId: string) {

  const db = getFirestore();

  const dRef = doc(db, "recruits", targetId + "_2027");
  const dSnap = await getDoc(dRef);

  if (!dSnap.exists()) {
    console.error("対象ドキュメントが存在しません");
    return;
  }

  const data = dSnap.data() || {};

  // 求人票専有のデータ(給与・募集区分・ハッシュタグ・応募情報・会社情報)は、年度別の求人票の
  // うち入力完了済みの最新年度を優先し、無ければ本体(旧フロー)を使う。
  // 求人票の確定処理(searchIndexSync.ts)と同じ選び方。以前は本体だけを読んでいたため、
  // 年度別の求人票にしか入力が無い会社では募集区分・ハッシュタグ・業種が空になっていた。
  const latestJobPosting = await findLatestJobPosting(targetId);
  const jobPosting = (key: string) => latestJobPosting?.[key] ?? data[key];

  let searchSummary: string[] = [];

  // --------------------
  // 給与 salaryTags
  // --------------------
  const salaryTags: string[] = [];

  // 求人票を一度も作っていない会社(求人ページだけ公開)は employment20 が無い。
  // 以前はここで落ちて searchIndex が作られず、公開したのにどこにも表示されなかった。
  const employment = jobPosting("employment20") ?? {};

  const total =
    employment.salaryType === "年俸制"
      ? [
          Number(employment.baseSalary),
          Number(employment.regularAllowance?.price1) * 12,
          Number(employment.regularAllowance?.price2) * 12,
          Number(employment.fixedOvertime?.price) * 12,
        ]
          .filter((v) => !isNaN(v))
          .reduce((sum, v) => sum + v, 0)
      : [
          Number(employment.baseSalary),
          Number(employment.regularAllowance?.price1),
          Number(employment.regularAllowance?.price2),
          Number(employment.fixedOvertime?.price),
        ]
          .filter((v) => !isNaN(v))
          .reduce((sum, v) => sum + v, 0);

  // 万円単位
  const salaryMan = Math.floor(total / 10000);

  // 下限
  const start = 17;

  // 上限
  const end = Math.max(salaryMan, 17);

  for (let i = start; i <= end; i++) {
    salaryTags.push(i.toString());
  }

  var storeCount = 0;

  let storeImage = "";
  let storeTags: string[] = [];
  let storePentagonHash: string[] = [];
  let storeType: string[] = [];
  let firstStore: any = null;

  const storesQ = query(
    collection(db, "stores"),
    where("companyId", "==", targetId)
  );

  const storesSnap = await getDocs(storesQ);

  storesSnap.forEach((storeDoc) => {
    const storeData = storeDoc.data();
    if (!storeData) return;

    storeCount = storeCount + 1;

    if (storeData.number === 1) {
      storeImage = storeDoc.id;
      firstStore = storeData;
    }

    const storeName = cutAfterTenRough(storeData.name);
    searchSummary.push(storeName);

    storeTags.push(...(storeData.stationTags ?? []));
    storePentagonHash.push(...(storeData.pentagonHash ?? []));
    storeType.push(storeData.type);
  });

  // --------------------
  // summary / point / strong / info
  // --------------------
  const s = data.summary || {};
  const p = data.point || {};
  const st = data.strong || {};
  const i = jobPosting("info") || {};

  const texts: string[] = [];

  pushSafe(texts, s.title);
  pushSafe(texts, s.companyPr);

  // ポイント
  pushSafe(texts, p.title1);
  pushSafe(texts, p.title2);
  pushSafe(texts, p.title3);

  // strong
  pushSafe(texts, st.item1?.title);
  pushSafe(texts, st.item1?.desc);
  pushSafe(texts, st.item2?.title);
  pushSafe(texts, st.item2?.desc);
  pushSafe(texts, st.item3?.title);
  pushSafe(texts, st.item3?.desc);

  const searchText = texts.join(" ");

  // キーワード抽出
  const keywords = extractKeywords(searchText, textQuery);
  const searchKeywords = keywords;

  // searchSummary
  searchSummary = [
    s.name,
    i.companyName,
    i.companyNameKana,
    ...searchSummary,
  ];

  // ---- タグ再分類 ----
  const hashTag = jobPosting("hashTag");
  const tags1 = Array.isArray(hashTag?.tags1) ? hashTag.tags1 : [];
  const tags2 = Array.isArray(hashTag?.tags2) ? hashTag.tags2 : [];
  const tags3 = Array.isArray(hashTag?.tags3) ? hashTag.tags3 : [];

  const {
    hashTagsWorkStyle,
    hashTagsBenefit,
    hashTagsEducation,
  } = reclassifyTags(tags1, tags2, tags3);

  const info = jobPosting("applicationInfo");

  const businessMap: Record<string, number | undefined> = {
    理容: info?.recruitsBarber,
    美容: info?.recruitsBeauty,
    メイク: info?.recruitsMake,
    アイ: info?.recruitsEye,
    ネイル: info?.recruitsNeil,
    ブライダル: info?.recruitsBridal,
    エステ: info?.recruitsAesthetic,
  };

  const businessTags: string[] = [
    ...Object.entries(businessMap)
      .filter(([_, count]) => (count ?? 0) > 0)
      .map(([name]) => name),
  ];

  // 「その他」があれば追加
  if (i.business?.contents?.includes("その他")) {
    businessTags.push("その他");
  }

  if (i.business?.canConcurrent === true) {
    businessTags.push("兼任可能");
  }

  const sendingWhitelist = await loadSendingWhitelist();
  const hasFlags = await computeHasFlags(db, targetId);

  // --------------------
  // searchIndex 更新オブジェクト
  // --------------------
  const searchIndex = {
    id: targetId + "_2027",
    name: data.summary?.name,
    title: data.summary?.title,
    companyId: data.companyId,

    image: data.summary?.images?.[0] ?? "",
    storeImage: storeImage,

    ...hasFlags,

    jobPostingsStatus: data.jobPostingsStatus === 1,
    recruitStatus: true,

    randomValue: Math.random(),

    person: toSearchIndexPerson(data.person),

    searchSummary: searchSummary,
    searchKeywords: searchKeywords,

    salaryTags: salaryTags,
    business: businessTags,

    storeTags: storeTags,
    pentagonHash: storePentagonHash,
    storeType: storeType,
    addressPref: addressPrefOf(i, firstStore),

    hashTagsEducation,
    hashTagsBenefit,
    hashTagsWorkStyle,

    // 募集区分タブ(4.target.tsx)は「専門(20歳卒)」を外せないUIなので、タブ自体が
    // 未保存(target.targetが空)の会社も専門(20歳卒)は対象として扱う(2026-11-04)。
    target: jobPosting("target")?.target?.length > 0 ? jobPosting("target").target : ['専門(20歳卒)'],

    storeCount: storeCount,

    sending_2027: (data["2027_sending"] ?? []).filter((id: string) => sendingWhitelist.has(id)).filter((id: string, i: number, arr: string[]) => arr.indexOf(id) === i),
    sending_2028: (data["2028_sending"] ?? []).filter((id: string) => sendingWhitelist.has(id)).filter((id: string, i: number, arr: string[]) => arr.indexOf(id) === i),
  };

  dedupeObjectArrays(searchIndex);

  await setDoc(doc(db, "searchIndex", targetId + "_2027"), searchIndex, {
    merge: true,
  });
}

export const textQuery = [
  "ハイトーン", "ブリーチ", "ダブルカラー", "トリプルカラー", "デザインカラー", "バレイヤージュ", "インナーカラー", "グラデーションカラー", "イヤリングカラー", "フェイスフレーミング", "エフェクトブリーチ", "ハイライト", "ローライト", "透明感カラー", "酸性カラー", "ケアブリーチ", "黒染め落とし", "カラー特化", "外国人風カラー", "韓国風カラー", "メンズブリーチ", "メンズカラー", "カラーモデル", "カラー講習", "カラー理論", "カラー技術", "薬剤知識", "髪質改善カラー", "カラーコントロール", "色落ちコントロール", "カラーシミュレーション", "白髪染め", "白髪ぼかし", "ブリーチ比率高め", "カラー強いサロン", "カット", "メンズカット", "ショートカット", "レイヤーカット", "ウルフカット", "似合わせカット", "再現性カット", "質感調整", "毛量調整", "パーマ", "デジタルパーマ", "ツイストパーマ", "スパイラルパーマ", "メンズパーマ", "ナチュラルパーマ", "ストレート", "縮毛矯正", "前髪カット", "トップノッチ", "フィニッシュワーク", "スタイリング技術", "コテ巻き", "アイロン技術", "ブロー技術", "カット専門", "カット特化", "バリカン技術", "メンズ特化", "美容技術", "デザイン力", "技術力高い", "指名100万", "高単価サロン", "集客力高い", "カット比率高め", "技術練習し放題", "ウィッグ代補助", "教育カリキュラム", "選択型カリキュラム", "新人研修", "アカデミー制度", "営業時間内レッスン", "営業内講習", "業務内練習", "営業中レッスン", "モデルカット", "技術指導", "専任トレーナー", "外部講師", "セミナー多数", "講習会", "撮影講習", "メイク講習", "カラー講習", "パーマ講習", "動画学習", "自社アプリ", "教育動画", "ロンドン研修", "海外研修", "独立支援", "店舗合同練習", "スタジオ研修", "国際美容技能証明書", "カミカリスマ受賞", "実技試験", "技術チェック", "デビュー最短6ヶ月", "デビュー9ヶ月", "デビュースピード早い", "指名売上育成", "ロードマップ制度", "キャリア形成", "研修特化サロン", "アカデミー店舗", "美容専門スクール", "クリエイティブチーム", "トータルビューティー", "ヘアメイク", "メイクアップ", "着付け", "振袖着付け", "袴着付け", "ブライダル", "花嫁ヘア", "アイリスト", "マツエク", "マツパ", "ネイリスト", "ジェルネイル", "アイブロウ", "撮影スキル", "作品撮り", "フォトシュート", "モデル撮影", "SNS撮影", "サロン撮影機材", "スタジオ撮影", "TGC出演", "NYLON掲載", "ar掲載", "雑誌掲載", "ビューティー撮影", "広告撮影", "PV撮影", "イベント撮影", "福利厚生充実", "社会保険完備", "住宅手当", "家賃補助", "引っ越し金", "一人暮らし支援", "単身寮", "帰省休暇", "有休しっかり", "産休制度", "育休制度", "時短勤務", "年間休日120日以上", "ワークライフバランス", "土日休み可", "長期休日", "賞与あり", "評価制度", "MVP制度", "スキル評価", "月給24万以上", "月給23万以上", "高収入", "月給30万可能", "時給制", "昇給制度", "通勤手当", "社員旅行", "社内イベント", "BBQイベント", "推し活休暇", "メンタルケア", "福利厚生サポート", "長く働ける", "離職率低い", "人間関係良好", "仲良いサロン", "アットホーム", "チームワーク", "夢を応援", "夢を叶える", "コミュニケーション重視", "技術重視", "オールマイティー", "再現性重視", "創造性重視", "理念共感", "行動力", "まず考える", "とにかく行動", "学び合う文化", "成長環境", "先輩が優しい", "面倒見がいい", "風通し良い", "成長スピード早い", "実力主義", "チームリーダー", "トッププレイヤー", "挑戦できる環境", "夢サポート", "個性尊重", "多様性あるサロン", "集客力", "新規客多数", "リピート率高い", "来客数1000人", "ブリーチ比率90%", "顧客単価高い", "高単価サロン", "人気サロン", "SNS人気サロン", "フォロワー多数", "TikTok強い", "Instagram強い", "インフルエンサー在籍", "バズる施術", "口コミ高評価", "ホットペッパーランキング", "指名多数", "客層広い", "若者向け", "大人女性向け", "セレブ客多い", "10代人気", "20代人気", "学生人気", "ハイセンススタイル", "最新トレンド", "韓国風スタイル", "外国人風スタイル", "業界トップクラス", "カミカリスマ受賞", "受賞歴多数", "ヘアショー出演", "商品開発", "薬剤開発",
]

// =====================

function pushSafe(arr: string[], value?: string | null) {
  if (value && value.trim() !== "") {
    arr.push(value);
  }
}

// recruits/{companyId}_2027/jobPostings 配下から、実際に企業情報(employment20)が
// 入っている最も新しい年度のドキュメントを選ぶ。年度はドキュメントID
// (例: "2027" "2028")を数値として比較する。
// employment20 は年度タブ追加・年度コピーの時点で書きかけのまま作られるため、
// isCompleted === true の年度だけを候補にする(公開求人ページと同じ判定基準)。
// 求人票の確定処理(searchIndexSync.ts)と求人ページの公開処理の両方で使う。
export async function findLatestJobPosting(companyId: string): Promise<any> {
  const db = getFirestore();

  const snap = await getDocs(jobPostingYearCollection(db, companyId));

  let latestYear = -1;
  let latestData: any = null;

  snap.forEach((docSnap) => {
    const year = Number(docSnap.id);
    if (Number.isNaN(year)) return;

    const data = docSnap.data();
    if (!data || data.employment20?.isCompleted !== true) return; // 未入力・書きかけの年度は候補にしない

    if (year > latestYear) {
      latestYear = year;
      latestData = data;
    }
  });

  return latestData;
}

// searchIndex の person(人物像)。学生アプリ(memorii lib/9.type/search_recruits.dart)と
// functions(aiScoringJob / aiRecruitAnalysisJob)は、各軸を { left, right } のマップで読む。
// 以前は [left, right] の配列で書いていたため、最新の学生アプリでは全軸0と扱われ、
// マッチングから除外されていた。recruits.person の isCompleted / time は含めない。
const PERSON_AXES = ['H_C', 'T_E', 'J_P', 'S_R'] as const;

// 定義のバージョン(personAxes.ts)があれば一緒にコピーする。学生アプリは求人ごとにこの値で軸の意味を判定する
// (無ければ 1 = 今の定義として読む)。
// 2026-10-20: version>=2(新方式)のときは state/value も一緒にコピーする
// (学生アプリがAlgolia経由でstate/valueを読むため。これを落とすと「こだわらない」
// 「中間」の区別がAlgolia上で失われ、新方式のマッチ率が計算できなくなる)。
export const toSearchIndexPerson = (person: any) => {
  const isV2 = Number.isInteger(person?.version) && person.version >= 2;
  return {
    ...(Object.fromEntries(PERSON_AXES.map((axis) => {
      const a = person?.[axis];
      const base: Record<string, unknown> = {
        left: Number(a?.left ?? 0),
        right: Number(a?.right ?? 0),
      };
      if (isV2) {
        base.state = typeof a?.state === 'string' ? a.state : 'unset';
        if (typeof a?.value === 'number') base.value = a.value;
      }
      return [axis, base];
    }))),
    ...(Number.isInteger(person?.version) && person.version >= 1 ? { version: person.version as number } : {}),
  };
};

export const dedupeObjectArrays = (obj: any) => {
  Object.keys(obj).forEach((key) => {
    const v = obj[key];
    if (Array.isArray(v)) {
      obj[key] = [...new Set(
        v.filter((x) => {
          // null / undefined / 空文字 / 空白だけ → 除外
          if (x === null || x === undefined) return false;
          if (typeof x === "string" && x.trim() === "") return false;
          return true;
        })
      )];
    }
  });
};


// 文章検索

function normalizeStr(text: string): string {
  return text
    .normalize("NFKC")        // 全角・半角の統一
    .replace(/\s+/g, "")      // 全ての空白を除去（重要）
    .toLowerCase();           // 小文字化（英語対策）
}

export function extractKeywords(searchText: string, textQuery: string[]) {
  if (!searchText) return [];

  const text = normalizeStr(searchText);

  const keywords: string[] = [];

  for (const word of textQuery) {
    const w = normalizeStr(word);

    if (text.includes(w)) {
      keywords.push(word);
    }
  }

  return keywords;
}

// 店舗名抜き出し
export function cutAfterTenRough(name: string): string {
  const idx = name.indexOf("店");
  if (idx === -1) return name.trim();

  const before = name.substring(0, idx).trim();

  const lastSpace = before.lastIndexOf(" ");
  if (lastSpace !== -1) {
    return before.substring(0, lastSpace).trim();
  }

  return before;
}

export function reclassifyTags(tags1: string[], tags2: string[], tags3: string[]) {
  // ① 全て統合して重複除去
  const allTags = Array.from(new Set([...tags1, ...tags2, ...tags3]));

  // ② 出力用
  const result1: string[] = [];
  const result2: string[] = [];
  const result3: string[] = [];

  // ③ 自動分類
  for (const tag of allTags) {
    if (HASH_TAG_1.includes(tag)) {
      result1.push(tag);
    } else if (HASH_TAG_2.includes(tag)) {
      result2.push(tag);
    } else if (HASH_TAG_3.includes(tag)) {
      result3.push(tag);
    }
  }

  return {
    hashTagsWorkStyle: result1,  // HASH_TAG_1
    hashTagsBenefit: result2,    // HASH_TAG_2
    hashTagsEducation: result3,  // HASH_TAG_3
  };
}

// 本社の都道府県。searchIndex.addressPref に入れ、アプリの求人一覧(リスト表示)のアイコンに使う。
// 決め方: 会社情報で選んだ都道府県(info.addressPref) → 会社情報の住所(info.address) → 1店舗目(stores.number === 1)の住所・最寄り駅の都道府県。
// どれも無ければ ''。functions/scripts/backfillAddressPref.cjs も同じ決め方。
export function addressPrefOf(info: any, firstStore?: any): string {
  const pref = info?.addressPref;
  if (typeof pref === 'string' && PREFECTURES.includes(pref)) return pref;
  return (
    prefFromAddress(info?.address) ||
    prefFromAddress(firstStore?.address) ||
    ((firstStore?.stationTags ?? []) as string[]).find((t) => PREFECTURES.includes(t)) ||
    ''
  );
}

// 学校側の求人一覧(searchIndex.sending_{year})に反映する学校 = schools コレクションにある学校。
// functions/school-sending.js も同じく schools コレクションを許可リストにしている。
// 読み込みに失敗したときは例外を投げる(空の許可リストで sending_{year} を上書きして消さないため)。
let sendingWhitelistCache: Promise<Set<string>> | null = null;
export function loadSendingWhitelist(): Promise<Set<string>> {
  if (!sendingWhitelistCache) {
    sendingWhitelistCache = getDocs(collection(getFirestore(), 'schools'))
      .then((snap) => new Set(snap.docs.map((d) => d.id)))
      .catch((e) => {
        sendingWhitelistCache = null;
        throw e;
      });
  }
  return sendingWhitelistCache;
}

const HASH_TAG_1 = [
  "年間休日120日以上",
  "完全週休2日制",
  "残業月20時間以内",
  "20時までに退勤可能",
  "有給休暇消化率90%以上",
  "配属店舗の選択可",
  "時短勤務可",
];

const HASH_TAG_2 = [
  "社会保険完備",
  "ボーナス支給実績あり",
  "車・バイク出勤可",
  "寮・社宅あり",
  "住宅手当あり",
  "育児休暇あり",
  "国内就労ビザ取得支援あり（留学生向け）",
];

const HASH_TAG_3 = [
  "デビューまで2年以内",
  "SNS研修あり",
  "自社アカデミーあり",
  "動画研修制度あり",
  "セミナー参加費補助",
  "ウィッグ代補助",
  "奨学金返済サポートあり",
  "営業中の練習OK",
  "資格取得支援",
  "社内コンテストあり",
  "複数の教育体制から選択可",
  "複数のキャリアプランを選択可",
  "独立・開業支援あり",
  "海外研修あり",
];
